export type {
  LivePanelVisibilityEntry,
  LivePanelVisibilityRegistry,
} from '@/common/utils/panelVisibilityRegistry'
export { applyPanelVisibilityInRegistry } from '@/common/utils/panelVisibilityRegistry'

export type FormShellVisibilityCleanupOptions = {
  slotId?: string
  registryFormIds?: string[]
  mapsOnly?: boolean
}

/**
 * Live panel setVisible is not a Label shell. mapsOnly / empty-formId wipe must keep it
 * so mounted Panel instances stay reachable from applyPanelVisibility.
 */
export function panelsVisibilityRegistryAfterShellCleanup<T>(registry: T): T {
  return registry
}

export function resolveFormSchemaChangeShellCleanup(input: {
  slotId: string
  oldSchemaId: number
  newSchemaId: number
}): FormShellVisibilityCleanupOptions | null {
  const slotId = String(input.slotId ?? '').trim()
  if (!slotId) return null
  const oldSchemaId = Number(input.oldSchemaId)
  const newSchemaId = Number(input.newSchemaId)
  if (!(oldSchemaId > 0 && newSchemaId > 0)) return null
  if (String(oldSchemaId) === String(newSchemaId)) return null
  return {
    slotId,
    registryFormIds: [String(oldSchemaId), String(newSchemaId)],
    mapsOnly: true,
  }
}

/** Same-schema reopen / new session: drop Label shell so onLoad hide is not blocked. */
export function resolvePageSlotShellCleanup(input: {
  slotId: string
  schemaId?: number
}): FormShellVisibilityCleanupOptions | null {
  const slotId = String(input.slotId ?? '').trim()
  if (!slotId) return null
  const registryFormIds = [slotId]
  const schemaId = Number(input.schemaId)
  if (schemaId > 0 && String(schemaId) !== slotId) {
    registryFormIds.push(String(schemaId))
  }
  return {
    slotId,
    registryFormIds,
    mapsOnly: true,
  }
}

const shellElementIdsBySlot = new Map<string, Set<string>>()

export function rememberShellElementIdForSlot(
  slotId: string | null | undefined,
  elementId: string | number,
): void {
  const slot = String(slotId ?? '').trim()
  const id = String(elementId ?? '').trim()
  if (!slot || !id) return
  let ids = shellElementIdsBySlot.get(slot)
  if (!ids) {
    ids = new Set()
    shellElementIdsBySlot.set(slot, ids)
  }
  ids.add(id)
}

export function collectIndexedShellElementIdsForCleanup(
  options?: FormShellVisibilityCleanupOptions,
): Set<string> {
  const ids = new Set<string>()
  const formIds = buildShellCleanupFormIdSet(options)
  if (formIds.size === 0) return ids
  for (const slot of formIds) {
    const indexed = shellElementIdsBySlot.get(slot)
    if (!indexed) continue
    for (const id of indexed) ids.add(id)
  }
  return ids
}

export function forgetIndexedShellElementIds(options?: FormShellVisibilityCleanupOptions): void {
  const formIds = buildShellCleanupFormIdSet(options)
  if (formIds.size === 0) {
    shellElementIdsBySlot.clear()
    return
  }
  for (const slot of formIds) {
    shellElementIdsBySlot.delete(slot)
  }
}

export function resetShellElementIdSlotIndexForTests(): void {
  shellElementIdsBySlot.clear()
}

export type ShellVisibilityRegistryEntry = {
  formId?: string | number
  pageDataSlot?: string
  elementId?: string | number
  fieldName?: string
}

export type ShellVisibilityCleanupTargets = {
  elementIds: Set<string>
  registryKeys: Set<string>
  deferredNameKeys: Set<string>
}

let cleanupHook: ((options?: FormShellVisibilityCleanupOptions) => void) | null = null

export function registerFormShellVisibilityCleanup(
  fn: (options?: FormShellVisibilityCleanupOptions) => void,
): void {
  cleanupHook = fn
}

/** Clears GlobalProvider element/panel shell visibility maps for a closed dialog slot. */
export function clearFormShellVisibilityRuntime(
  options?: FormShellVisibilityCleanupOptions,
): void {
  cleanupHook?.(options)
}

export function buildShellCleanupFormIdSet(
  options?: FormShellVisibilityCleanupOptions,
): Set<string> {
  const formIds = new Set<string>()
  for (const rawId of options?.registryFormIds ?? []) {
    const id = String(rawId ?? '').trim()
    if (id) formIds.add(id)
  }
  const slotId = String(options?.slotId ?? '').trim()
  if (slotId) formIds.add(slotId)
  return formIds
}

export function scopedDeferredNameKey(
  name: string,
  scopeFormId?: string | number | null,
): string {
  const base = String(name ?? '').trim()
  if (!base) return ''
  const scope = String(scopeFormId ?? '').trim()
  return scope ? `${scope}::${base}` : base
}

function collectDeferredNameKeysForEntry(
  registryKey: string,
  entry: ShellVisibilityRegistryEntry,
  deferredNameKeys: Set<string>,
): void {
  deferredNameKeys.add(registryKey)
  deferredNameKeys.add(scopedDeferredNameKey(registryKey, entry.formId))
  deferredNameKeys.add(scopedDeferredNameKey(registryKey, entry.pageDataSlot))

  const fieldName = String(entry.fieldName ?? '').trim()
  if (!fieldName) return

  deferredNameKeys.add(fieldName)
  deferredNameKeys.add(scopedDeferredNameKey(fieldName, entry.formId))
  deferredNameKeys.add(scopedDeferredNameKey(fieldName, entry.pageDataSlot))
}

function entryBelongsToShellCleanup(
  entry: ShellVisibilityRegistryEntry,
  slotId: string,
  formIds: Set<string>,
): boolean {
  const entrySlot = String(entry.pageDataSlot ?? '').trim()
  const entryFormId = String(entry.formId ?? '').trim()
  return Boolean(
    (slotId && entrySlot === slotId) ||
      (entryFormId && formIds.has(entryFormId)) ||
      (entrySlot && formIds.has(entrySlot)),
  )
}

function collectLiveRegistryEntries(
  source: Record<string, ShellVisibilityRegistryEntry | undefined> | null | undefined,
  into: ShellVisibilityRegistryEntry[],
): void {
  for (const entry of Object.values(source ?? {})) {
    if (entry && typeof entry === 'object') into.push(entry)
  }
}

/** Collect element ids / registry keys belonging to a closing dialog slot. */
export function collectShellVisibilityCleanupTargets(
  elements: Record<string, ShellVisibilityRegistryEntry | undefined> | null | undefined,
  options?: FormShellVisibilityCleanupOptions,
  liveByKey?: Record<string, ShellVisibilityRegistryEntry | undefined> | null,
): ShellVisibilityCleanupTargets {
  const formIds = buildShellCleanupFormIdSet(options)
  const slotId = String(options?.slotId ?? '').trim()
  const elementIds = new Set<string>()
  const registryKeys = new Set<string>()
  const deferredNameKeys = new Set<string>()

  if (formIds.size === 0 && !slotId) {
    return { elementIds, registryKeys, deferredNameKeys }
  }

  const nameEntries: ShellVisibilityRegistryEntry[] = []
  for (const [registryKey, entry] of Object.entries(elements ?? {})) {
    if (!entry || typeof entry !== 'object') continue
    nameEntries.push(entry)
    if (!entryBelongsToShellCleanup(entry, slotId, formIds)) continue

    registryKeys.add(registryKey)
    collectDeferredNameKeysForEntry(registryKey, entry, deferredNameKeys)
    if (entry.elementId != null && String(entry.elementId).trim() !== '') {
      elementIds.add(String(entry.elementId).trim())
    }
  }

  for (const id of collectIndexedShellElementIdsForCleanup(options)) {
    elementIds.add(id)
  }

  const liveEntries: ShellVisibilityRegistryEntry[] = [...nameEntries]
  collectLiveRegistryEntries(liveByKey, liveEntries)

  // Shared xml ids (same panel on parent + dialog): keep shell if another live slot still owns the id.
  // Parent may exist only in elementsByKey after dialog overwrites elements[name#id].
  for (const entry of liveEntries) {
    if (entryBelongsToShellCleanup(entry, slotId, formIds)) continue
    const id = entry.elementId != null ? String(entry.elementId).trim() : ''
    if (id) elementIds.delete(id)
  }

  return { elementIds, registryKeys, deferredNameKeys }
}

export function pruneRecordByKeys<T>(
  source: Record<string, T>,
  keysToRemove: Set<string>,
): Record<string, T> {
  if (keysToRemove.size === 0) return source
  let changed = false
  const next: Record<string, T> = { ...source }
  for (const key of keysToRemove) {
    if (Object.prototype.hasOwnProperty.call(next, key)) {
      delete next[key]
      changed = true
    }
  }
  return changed ? next : source
}

export function pruneRecordByElementIds<T>(
  source: Record<string, T>,
  elementIds: Set<string>,
): Record<string, T> {
  if (elementIds.size === 0) return source
  return pruneRecordByKeys(source, elementIds)
}

export function pruneVisibilityHooksByElementIds(
  hooks: Map<string, (visible: boolean) => void>,
  elementIds: Set<string>,
): void {
  for (const id of elementIds) {
    hooks.delete(id)
  }
}
